import {map, tidy} from '@tidyjs/tidy';

/**
 * Calculates percentage breakdowns for a given total and breakdown list.
 *
 * The output ensures that the sum of the percentages adds up to 100%.
 * Percentages are rounded to the nearest integer, and any percentage less than 1% is formatted as "<1%".
 *
 * @param total - The total number representing the sum of all items.
 * @param breakdown - An array representing the breakdown of each component.
 * @returns An array of percentages formatted as strings or numbers, summing up to 100%.
 *
 * @example
 *
 * const total = 1000;
 * const breakdown = [450, 320, 180, 50];
 * const result = calculatePercentages(total, breakdown);
 * console.log(result); // Example output: [45, 32, 18, 5]
 */
export const calculatePercentages = ({
  breakdown,
  total,
}: {
  total: number;
  breakdown: Array<number>;
}): Array<string | number> => {
  // Calculate initial percentages
  const percentages = breakdown.map(value => ({percentage: (value / total) * 100}));

  // Calculate the rounded sum
  const roundedSum = percentages.reduce((acc, {percentage: x}) => acc + Math.round(x), 0);

  // Calculate the offset needed to reach 100%
  const offset = 100 - roundedSum;

  // Adjust the percentages to add up to 100
  const adjustedPercentages = tidy(
    percentages,
    map(({percentage: x}, i) => Math.round(x) + (offset > i ? 1 : 0) - (i >= percentages.length + offset ? 1 : 0)),
  );

  // Format the output to include "<1%" for small percentages
  const formattedPercentages = adjustedPercentages.map(x => (x < 1 ? `<1%` : x));

  return formattedPercentages;
};
